import { decode, encode } from "isomorphic-textencoder";
import { v4 } from "uuid";

import { defined, notNull } from "@formant/core/common/defined";
import { delay } from "@formant/core/common/delay";
import { logger } from "@formant/core/common/logger";
import { stats } from "@formant/core/common/stats";
import { duration } from "@formant/core/model/duration";
import {
    rtcPingStreamType,
    rtcPingV2StreamType,
    rtcPongStreamType,
    rtcPongV2StreamType,
    rtcStreamControlStreamType,
    rtcStreamsInfoStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { Timeout } from "@formant/core/model/Timeout";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";
import { SessionType, Signal } from "@formant/protos";

import {
    heartbeatChannelConfig,
    heartbeatChannelLabel,
    latestReliableStreamConfig,
    latestReliableStreamLabel,
    latesTtlStreamLabel,
    latestTryOnceStreamConfig,
    latestTryOnceStreamLabel,
    latestTtlStreamConfig,
    reliableStreamConfig,
    reliableStreamLabel,
} from "./config";
import { gatherPingV2Metrics, PingMap } from "./gatherPingMetrics";
import { IHasPeerConnection } from "./IHasPeerConnection";
import { IHasRemoteDeviceId } from "./IHasRemoteDeviceId";
import {
    IBinaryChunkMessage,
    IBinaryH264Message,
} from "./models/IBinaryH264Message";
import { IPingInfo } from "./models/IPingInfo";
import { IPingPayload } from "./models/IPingPayload";
import { IRtcConnectionConfiguration } from "./models/IRtcConnectionConfiguration";
import { IRtcConnectionStatsInfo } from "./models/IRtcConnectionStatsInfo";
import { IRtcSendConfiguration } from "./models/IRtcSendConfiguration";
import { IRtcSessionMetricsMessageCounts } from "./models/IRtcSessionMetricsMessageCounts";
import { IRTCStatsIceCandidate } from "./models/IRTCStatsIceCandidate";
import { IRTCStatsIceCandidatePair } from "./models/IRTCStatsIceCandidatePair";
import { IRTCStatsTransport } from "./models/IRTCStatsTransport";
import { IRtcStreamCommunication } from "./models/IRtcStreamCommunication";
import { IRtcStreamMessage } from "./models/IRtcStreamMessage";
import { IRtcStreamMessageChunk } from "./models/IRtcStreamMessageChunk";
import { IRtcStreamPayload } from "./models/IRtcStreamPayload";
import { IStreamControl } from "./models/IStreamControl";
import { RtcSessionMetrics } from "./RtcSessionMetrics";
import { assertHasPeerConnection } from "./utils/assertHasPeerConnection";
import {
    isBinaryChunkMessage,
    isBinaryH264Message,
    parseBinaryChunkMessage,
    parseBinaryH264Message,
} from "./utils/binaryMessageParser";
import { createRtcStreamMessage } from "./utils/createRtcStreamMessage";
import { encodeStreamId } from "./utils/encodeStreamId";
import { iceModeFromRtcConnectionStatsInfo } from "./utils/iceModeFromRtcConnectionInfo";
import { isInactive } from "./utils/isInactive";
import { reassembleMessageChunks } from "./utils/reassembleMessageChunks";
import { reifyZeroValues } from "./utils/reifyZeroValues";

export class RtcConnection implements IHasPeerConnection, IHasRemoteDeviceId {
    private readonly connectTimeoutMs = 20 * duration.second;
    private readonly iceGatheringTimeoutMs = 3 * duration.second;
    private readonly pingUpdateTimeoutMs = 1 * duration.second;
    private readonly pingV2UpdateTimeoutMs = 250 * duration.millisecond;
    private readonly pingV2MetricsGatherTimeoutMs = 1 * duration.second;
    private readonly streamsInfoUpdateTimeoutMs = 2 * duration.second;
    // If we have not received all chunks for a message within reassemblyTimeoutMs,
    // drop all chunks
    private readonly reassemblyTimeoutMs = 500 * duration.millisecond;
    private readonly reassemblyTableCleanupMs = 1 * duration.second;
    private readonly heartbeatTimeoutMs = 20 * duration.millisecond;

    private _peerConnection: RTCPeerConnection | null = null;

    public get peerConnection(): RTCPeerConnection {
        assertHasPeerConnection(this._peerConnection);
        return this._peerConnection;
    }

    // high-frequency small-size heartbeat is sent over this data channel
    private heartbeatChannel: RTCDataChannel | null = null;
    // p2p RTC messages are sent over the following data channels
    private latestTtlStreamChannel: RTCDataChannel | null = null;
    private reliableStreamChannel: RTCDataChannel | null = null;
    private latestReliableStreamChannel: RTCDataChannel | null = null;
    private latestTryOnceStreamChannel: RTCDataChannel | null = null;

    private streamLatestTimestamp = new Map<string, Timestamp>();
    private reassemblyTable = new Map<number, IRtcStreamMessageChunk[]>();
    private reassemblyTableLastTimestamp = new Map<number, Timestamp>();
    private binaryReassemblyTable = new Map<number, Map<number, Uint8Array>>();
    private binaryReassemblyTableLastTimestamp = new Map<number, Timestamp>();
    private pingTimeMs: number | undefined;
    private lastMessageTimestamp: Timestamp | undefined;
    private sessionMetrics: RtcSessionMetrics | undefined;
    private closeCalled = false;
    private gotOffer = false;
    private hasIceCandidate = false;
    private pingV2Map: PingMap = new Map();
    private pingInfo: IPingInfo | undefined;

    private readonly reassemblyTableCleanupTimeout: Timeout;
    private readonly pingUpdateTimeout: Timeout;
    private readonly pingV2UpdateTimeout: Timeout;
    private readonly pingV2MetricsGatherTimeout: Timeout;
    private readonly streamsInfoUpdateTimeout: Timeout;
    private readonly heartbeatTimeout: Timeout;

    constructor(
        peerConnection: RTCPeerConnection,
        private iceServers: RTCIceServer[],
        private config: IRtcConnectionConfiguration,
        private dataChannelNotifier: (channel: RTCDataChannel) => void,
    ) {
        this._peerConnection = peerConnection;
        const { isOffer } = config.baseConfig;

        if (isOffer) {
            this.initializeChannels(peerConnection);
        } else {
            peerConnection.ondatachannel = _ => {
                switch (_.channel.label) {
                    case latesTtlStreamLabel:
                        this.latestTtlStreamChannel = _.channel;
                        break;
                    case reliableStreamLabel:
                        this.reliableStreamChannel = _.channel;
                        break;
                    case latestReliableStreamLabel:
                        this.latestReliableStreamChannel = _.channel;
                        break;
                    case latestTryOnceStreamLabel:
                        this.latestTryOnceStreamChannel = _.channel;
                        break;
                    case heartbeatChannelLabel:
                        this.heartbeatChannel = _.channel;
                        // Not implemented: handling heartbeat messages on the frontend
                        return;
                    default:
                        this.dataChannelNotifier(_.channel);
                        return;
                }
                this.setupChannel(_.channel);
            };
        }

        // Periodically ping the other side of the connection
        this.pingUpdateTimeout = setInterval(() => {
            const { reliableStreamChannel } = this;
            if (!reliableStreamChannel) {
                return;
            }
            this.sendSystemMessage(reliableStreamChannel, {
                type: rtcPingStreamType,
            });
        }, this.pingUpdateTimeoutMs);

        this.pingV2UpdateTimeout = setInterval(
            this.sendPingV2,
            this.pingV2UpdateTimeoutMs,
        );

        this.pingV2MetricsGatherTimeout = setInterval(
            this.gatherPingV2Metrics,
            this.pingV2MetricsGatherTimeoutMs,
        );

        // Periodically exchange streams info with the other side of the connection
        this.streamsInfoUpdateTimeout = setInterval(() => {
            const { latestTtlStreamChannel } = this;
            if (!latestTtlStreamChannel) {
                return;
            }
            this.sendSystemMessage(latestTtlStreamChannel, {
                type: rtcStreamsInfoStreamType,
            });
        }, this.streamsInfoUpdateTimeoutMs);

        // Periodically remove entries from the chunk reassembly table
        // that have been taking too long (some chunks were probably lost).
        this.reassemblyTableCleanupTimeout = setInterval(() => {
            const now = new Date().getTime();
            this.reassemblyTableLastTimestamp.forEach((key, value, _) => {
                if (now > value + this.reassemblyTimeoutMs) {
                    this.reassemblyTable.delete(key);
                }
            });
        }, this.reassemblyTableCleanupMs);

        this.heartbeatTimeout = setInterval(() => {
            const { heartbeatChannel } = this;
            if (!heartbeatChannel || heartbeatChannel.readyState !== "open") {
                return;
            }
            const heartbeatData = new Uint8Array([1]);
            heartbeatChannel.send(heartbeatData.buffer);
        }, this.heartbeatTimeoutMs);
    }

    // Handle incoming signal.
    // If the signal is an offer and there is an answer, return it.
    public async handleSignal(signal: Signal): Promise<Signal | undefined> {
        const { peerConnection } = this;
        const { track } = this.config.baseConfig;

        const description = (() => {
            try {
                return JSON.parse(signal.getPayload());
            } catch {
                return undefined;
            }
        })();
        if (!description) {
            logger.error("Received unparseable signal.");
            return;
        }

        logger.debug("Handling signal", { description });
        const { sdp, type } = description;
        if (!sdp || !type) {
            logger.warn("Received non-SDP signal");
            return;
        }

        const { signalingState, connectionState } = peerConnection;

        if (signalingState === "stable" && connectionState === "connected") {
            logger.warn(`Received SDP when already connected: ${sdp}`);
            return;
        }

        if (peerConnection.remoteDescription) {
            logger.warn(
                `Received SDP after remote description was set: ${sdp}`,
            );
            return;
        }

        if (type === "offer") {
            if (signalingState !== "stable") {
                logger.warn("Received offer SDP when signaling is ongoing.");
                return;
            }
            // The answer must reply to every offer.
            await peerConnection.setRemoteDescription(description);
            const answer = await peerConnection.createAnswer();
            await peerConnection.setLocalDescription(answer);
            // Create and return the answer signal.
            const answerSignal = signal.clone();

            track?.("Answer Received", answerSignal);

            answerSignal.setPayload(JSON.stringify(answer));
            answerSignal.setReceiverId(signal.getSenderId());
            answerSignal.setSenderId(signal.getReceiverId());
            return answerSignal;
        } else if (type === "answer") {
            if (signalingState === "stable") {
                logger.warn(
                    "Received answer SDP when signaling hasn't started.",
                );
                return;
            }
            await peerConnection.setRemoteDescription(description);
        }
    }

    /**
     * Sends a message to the remote peer.
     */
    public send(message: IRtcStreamMessage, config: IRtcSendConfiguration) {
        // All streams are enabled by default coming from a typescript client.
        const configuredChannel = this.getChannelFromLabel(config.channelLabel);

        if (configuredChannel) {
            this.sendOnChannel(configuredChannel, message);
            return;
        }

        logger.warn("Send called with unexpected channel label", {
            channelLabel: config.channelLabel,
        });
    }

    public controlRemoteStream(streamControl: IStreamControl) {
        this.sendSystemMessage(notNull(this.reliableStreamChannel), {
            type: rtcStreamControlStreamType,
            streamControl,
        });
    }

    public isActive() {
        return (
            new Set(["new", "checking", "connected", "completed"]).has(
                this.peerConnection.iceConnectionState,
            ) || this.isReady()
        );
    }

    /**
     * Checks that the peerConnection is connected
     * and all data channels exist and are open
     */
    public isReady() {
        const {
            reliableStreamChannel,
            latestTtlStreamChannel,
            latestTryOnceStreamChannel,
            latestReliableStreamChannel,
        } = this;

        return (
            reliableStreamChannel?.readyState === "open" &&
            latestTtlStreamChannel?.readyState === "open" &&
            latestTryOnceStreamChannel?.readyState === "open" &&
            latestReliableStreamChannel?.readyState === "open"
        );
    }

    public async close() {
        if (this.closeCalled) {
            return;
        }
        this.closeCalled = true;
        await this.sessionMetrics?.uploadMetrics();

        safeClearInterval(this.pingUpdateTimeout);
        safeClearInterval(this.pingV2UpdateTimeout);
        safeClearInterval(this.pingV2MetricsGatherTimeout);
        safeClearInterval(this.reassemblyTableCleanupTimeout);
        safeClearInterval(this.streamsInfoUpdateTimeout);
        safeClearInterval(this.heartbeatTimeout);

        this._peerConnection?.close();
        this.heartbeatChannel?.close();
        this.latestReliableStreamChannel?.close();
        this.latestTryOnceStreamChannel?.close();
        this.latestTtlStreamChannel?.close();
        this.reliableStreamChannel?.close();

        this._peerConnection = null;
        this.heartbeatChannel = null;
        this.latestReliableStreamChannel = null;
        this.latestTryOnceStreamChannel = null;
        this.latestTtlStreamChannel = null;
        this.reliableStreamChannel = null;
    }

    public getPing(): number | undefined {
        return this.pingTimeMs;
    }

    public getPingInfo(): IPingInfo | undefined {
        return this.pingInfo;
    }

    public getLastMessageTimestamp(): Timestamp | undefined {
        return this.lastMessageTimestamp;
    }

    public getSessionCreatedTimestamp(): Timestamp | undefined {
        return this.config.remoteConfig?.sessionCreatedTimestamp;
    }

    public setSessionCreatedTimestamp(sessionCreatedTimestamp: Timestamp) {
        if (this.config.remoteConfig) {
            this.config.remoteConfig.sessionCreatedTimestamp =
                sessionCreatedTimestamp;
        }
    }

    public getSessionId(): Uuid | undefined {
        return this.config.remoteConfig?.sessionId;
    }

    public setSessionId(sessionId: Uuid) {
        if (this.config.remoteConfig) {
            this.config.remoteConfig.sessionId = sessionId;
            this.sessionMetrics = new RtcSessionMetrics(sessionId, this);
        }
    }

    public getRemotePeerId(): Uuid | undefined {
        return this.config.baseConfig.remotePeerId;
    }

    public getRemoteDeviceId(): Uuid | undefined {
        return this.config.baseConfig?.remoteDeviceId;
    }

    public setRemoteDeviceId(deviceId: Uuid | undefined) {
        this.config.baseConfig.remoteDeviceId = deviceId;
    }

    public getSessionMetricsMessageCounts():
        | IRtcSessionMetricsMessageCounts
        | undefined {
        return this.sessionMetrics?.counts;
    }

    public async getConnectionStatsInfo(): Promise<
        IRtcConnectionStatsInfo | undefined
    > {
        const { peerConnection } = this;
        if (!peerConnection) {
            return;
        }
        const pcStats = await peerConnection.getStats(null);
        const reports: any[] = [];
        pcStats.forEach(report => {
            reports.push(report);
        });

        const transport: IRTCStatsTransport | undefined = reports.find(
            _ => _.type === "transport",
        );
        if (!transport) {
            return undefined;
        }

        const selectedPair: IRTCStatsIceCandidatePair | undefined =
            reports.find(
                _ =>
                    (_.type === "candidate-pair" ||
                        _.type === "candidatepair") &&
                    _.id === transport.selectedCandidatePairId,
            );
        if (!selectedPair) {
            return undefined;
        }
        const localCandidate: any = reports.find(
            _ => _.id === selectedPair.localCandidateId,
        );
        const remoteCandidate: any = reports.find(
            _ => _.id === selectedPair.remoteCandidateId,
        );

        if (!localCandidate || !remoteCandidate) {
            return undefined;
        }

        // Chrome: "ip"
        // Safari: "ip"
        // Firefox: "address"
        // Phones (Chrome, Safari iOS) may have a different/missing value: TODO
        localCandidate.address = localCandidate.address || localCandidate.ip;
        remoteCandidate.address = remoteCandidate.address || remoteCandidate.ip;
        if (
            localCandidate.address === undefined ||
            remoteCandidate.address === undefined
        ) {
            return undefined;
        }

        return {
            transport,
            localCandidate: localCandidate as IRTCStatsIceCandidate,
            remoteCandidate: remoteCandidate as IRTCStatsIceCandidate,
            candidatePair: selectedPair as IRTCStatsIceCandidatePair,
        };
    }

    private initializeChannels(peerConnection: RTCPeerConnection) {
        this.heartbeatChannel = peerConnection.createDataChannel(
            heartbeatChannelLabel,
            heartbeatChannelConfig,
        );
        this.heartbeatChannel.binaryType = "arraybuffer";
        // Not implemented: handling heartbeat messages on the frontend

        this.latestTtlStreamChannel = peerConnection.createDataChannel(
            latesTtlStreamLabel,
            latestTtlStreamConfig,
        );
        this.latestTtlStreamChannel.binaryType = "arraybuffer";
        this.reliableStreamChannel = peerConnection.createDataChannel(
            reliableStreamLabel,
            reliableStreamConfig,
        );
        this.reliableStreamChannel.binaryType = "arraybuffer";
        this.latestReliableStreamChannel = peerConnection.createDataChannel(
            latestReliableStreamLabel,
            latestReliableStreamConfig,
        );
        this.latestReliableStreamChannel.binaryType = "arraybuffer";
        this.latestTryOnceStreamChannel = peerConnection.createDataChannel(
            latestTryOnceStreamLabel,
            latestTryOnceStreamConfig,
        );
        this.latestTryOnceStreamChannel.binaryType = "arraybuffer";
        this.setupChannel(this.latestTtlStreamChannel);
        this.setupChannel(this.reliableStreamChannel);
        this.setupChannel(this.latestReliableStreamChannel);
        this.setupChannel(this.latestTryOnceStreamChannel);
    }

    public async getOffer(): Promise<Signal | undefined> {
        const { gotOffer } = this;
        const { peerConnection, config } = this;

        if (this.config.baseConfig.isLan) {
            throw new Error(
                '"getOffer" method can only be called with internet connections. LAN connections should call the "getLanOffer" method.',
            );
        }

        const sessionId = this.config.remoteConfig?.sessionId;

        if (gotOffer) {
            logger.debug(
                "Failed to generate offer because gotOffer was already called.",
            );
            return;
        }
        this.gotOffer = true;

        if (isInactive(this.peerConnection)) {
            logger.debug(
                "Failed to generate offer because the peer connection was inactive.",
            );
            return;
        }

        const track = config.baseConfig.track;

        peerConnection.onicecandidate = () => {
            this.hasIceCandidate = true;
        };

        peerConnection.oniceconnectionstatechange = async () => {
            const iceConnectionState = peerConnection.iceConnectionState;
            if (
                iceConnectionState === "connected" ||
                iceConnectionState === "completed"
            ) {
                const connectionStatsInfo = await this.getConnectionStatsInfo();
                const iceMode = connectionStatsInfo
                    ? iceModeFromRtcConnectionStatsInfo(connectionStatsInfo)
                    : undefined;
                logger.info(
                    `ICE connection state changed to ${iceConnectionState}`,
                    {
                        sessionId,
                        connectionStatsInfo,
                        iceMode,
                    },
                );
                track?.("ICE connection state change", {
                    iceConnectionState,
                    sessionId,
                    connectionStatsInfo,
                    iceMode,
                });
            }
        };

        // Trigger ICE candidate gathering.
        await peerConnection.setLocalDescription(
            await peerConnection.createOffer(),
        );

        // getConfiguration is not supported in React Native WebRTC
        const iceTransportPolicy = peerConnection.getConfiguration
            ? peerConnection.getConfiguration().iceTransportPolicy
            : "all";

        // Wait for ICE gathering to complete, or timeouts to occur.
        const beginTime = new Date().getTime();
        while (true) {
            const waitTime = new Date().getTime() - beginTime;
            if (waitTime > this.connectTimeoutMs) {
                logger.debug(
                    "Failed to generate offer because ICE gathering timed out.",
                );
                return; // Failure
            }
            if (waitTime > this.iceGatheringTimeoutMs && this.hasIceCandidate) {
                logger.debug("ICE gathering partially completed; proceeding", {
                    iceTransportPolicy,
                    waitTime,
                });
                track?.("ICE gathering partially completed", {
                    sessionId,
                    iceTransportPolicy,
                    waitTime,
                });
                break;
            }
            if (peerConnection.iceGatheringState === "complete") {
                logger.debug("ICE gathering complete", {
                    iceTransportPolicy,
                    waitTime,
                });
                track?.("ICE gathering completed", {
                    sessionId,
                    iceTransportPolicy,
                    waitTime,
                });
                break;
            }
            await delay(0.1 * duration.second);
        }

        // By including ICE servers on the signal,
        // the answerer can use the same ICE servers as the offerer.
        // This allows us to "force" TURN or other configurations more easily.
        const iceServers = this.iceServers ?? [];

        // Normalize Chrome and Firefox-style ICE servers
        for (const server of iceServers) {
            // https://developer.mozilla.org/en-US/docs/Web/API/RTCIceServer/credentialType
            if ("credentialType" in server) {
                server.credentialType = undefined;
            }
        }

        const iceServersEncoded = JSON.stringify(iceServers);

        // Offer description will now contain ICE candidates
        const offerSignal = new Signal();
        offerSignal.setPayload(JSON.stringify(peerConnection.localDescription));
        offerSignal.setSenderId(this.config.baseConfig.localPeerId);
        offerSignal.setReceiverId(this.config.baseConfig.remotePeerId);
        offerSignal.setIceServers(iceServersEncoded);
        offerSignal.setIceTransportPolicy(iceTransportPolicy ?? "all");
        if (this.config.baseConfig.sessionType !== undefined) {
            offerSignal.setSessionType(this.config.baseConfig.sessionType);
        } else {
            offerSignal.setSessionType(SessionType.TELEOP);
        }
        const lockDeviceForOffer = this.config.baseConfig.lockDevice === true;
        offerSignal.setLockDevice(lockDeviceForOffer);

        const g = globalThis as unknown as {
            __formant_proto_debug?: boolean;
        };
        const formantProtoDebug =
            g.__formant_proto_debug === true ||
            (typeof process !== "undefined" &&
                process.env?.NODE_ENV !== "production");
        if (formantProtoDebug) {
            console.log(
                "[formant:proto] RtcConnection getOffer created Signal",
                {
                    signalConstructor: offerSignal.constructor?.name,
                    baseConfigLockDevice: this.config.baseConfig.lockDevice,
                    lockDeviceForOffer,
                    offerGetLockDevice: offerSignal.getLockDevice(),
                    hasSetLockDevice:
                        typeof offerSignal.setLockDevice === "function",
                },
            );
        }

        logger.debug("Sending offer signal with description", {
            description: offerSignal.getPayload(),
            baseConfigLockDevice: this.config.baseConfig.lockDevice,
            lockDeviceForOffer,
            offerGetLockDevice: offerSignal.getLockDevice(),
        });

        return offerSignal;
    }

    public async getLanOffer(): Promise<RTCSessionDescription | undefined> {
        const { peerConnection, gotOffer } = this;

        if (!this.config.baseConfig.isLan) {
            throw new Error(
                '"getLanOffer" method can only be used with LAN connections. Internet connections should call the "getOffer" method.',
            );
        }

        if (gotOffer) {
            logger.debug(
                "Failed to generate offer because gotOffer was already called.",
            );
            return;
        }
        this.gotOffer = true;

        if (isInactive(this.peerConnection)) {
            logger.debug(
                "Failed to generate offer because the peer connection was inactive.",
            );
            return;
        }

        // Trigger ICE candidate gathering.
        await peerConnection.setLocalDescription(
            await peerConnection.createOffer(),
        );

        // Wait for ICE gathering to complete, or timeout to occur.
        const beginTime = new Date().getTime();
        while (true) {
            const waitTime = new Date().getTime() - beginTime;
            if (waitTime > this.iceGatheringTimeoutMs) {
                logger.debug(
                    "Failed to generate offer because ICE gathering timed out.",
                );
                return; // Failure
            }
            if (peerConnection.iceGatheringState === "complete") {
                break;
            }
            await delay(0.1 * duration.second);
        }

        const description = peerConnection.localDescription;
        if (!description) {
            logger.error("Failed to generate LAN offer description");
            return;
        }

        logger.debug("Sending LAN offer signal with description", {
            description,
        });
        return description;
    }

    public async handleLanAnswer(answerSdp: RTCSessionDescription) {
        const { peerConnection } = this;

        if (!this.config.baseConfig.isLan) {
            throw new Error(
                '"handleLanAnswer" method can only be used with LAN connections. Internet connections should call the "handleSignal" method.',
            );
        }

        await peerConnection.setRemoteDescription(answerSdp);
    }

    private getChannelFromLabel(label: string) {
        switch (label) {
            case latesTtlStreamLabel:
                return this.latestTtlStreamChannel;
            case reliableStreamLabel:
                return this.reliableStreamChannel;
            case latestReliableStreamLabel:
                return this.latestReliableStreamChannel;
            case latestTryOnceStreamLabel:
                return this.latestTryOnceStreamChannel;
        }
    }

    private sendOnChannel(channel: RTCDataChannel, message: IRtcStreamMessage) {
        let encoded: Uint8Array;
        try {
            encoded = encode(JSON.stringify(message));
        } catch (error) {
            logger.warn("Failed to encode RTC message", { error });
            return;
        }

        try {
            // Create a new Uint8Array with a proper ArrayBuffer for WebRTC DataChannel.send()
            const sendData = new Uint8Array(encoded);
            channel.send(sendData.buffer);
            this.sessionMetrics?.incrementLocalSent(message);
        } catch (error) {
            logger.warn("Failed to send message to channel", {
                error,
                channel: channel.label,
            });
        }
    }

    private channelNotRecognized(channel: RTCDataChannel) {
        return (
            channel !== this.latestTtlStreamChannel &&
            channel !== this.latestReliableStreamChannel &&
            channel !== this.latestTryOnceStreamChannel &&
            channel !== this.reliableStreamChannel
        );
    }

    // Set up RTC stream data channel
    private setupChannel(channel: RTCDataChannel) {
        channel.onmessage = event => {
            if (this.channelNotRecognized(channel)) {
                logger.warn("Received message on unrecognized data channel.");
                return;
            }

            let data: IRtcStreamCommunication;

            // Check if this is a binary message first
            if (event.data instanceof ArrayBuffer) {
                try {
                    if (isBinaryH264Message(event.data)) {
                        const binaryMessage = parseBinaryH264Message(
                            event.data,
                        );
                        this.receiveBinaryH264Message(channel, binaryMessage);
                        return;
                    } else if (isBinaryChunkMessage(event.data)) {
                        const binaryChunk = parseBinaryChunkMessage(event.data);
                        this.receiveBinaryChunkMessage(channel, binaryChunk);
                        return;
                    }
                    // If it's an ArrayBuffer but not a recognized binary format, try to decode as text
                    const textData = decode(new Uint8Array(event.data));
                    data = JSON.parse(textData);
                } catch (error) {
                    logger.warn(
                        "Received unparseable binary message on RTC stream data channel",
                        {
                            error,
                            channel: channel.label,
                        },
                    );
                    return;
                }
            } else {
                // Handle text messages (existing JSON format)
                try {
                    const textData =
                        typeof event.data === "string"
                            ? event.data
                            : decode(
                                  event.data instanceof ArrayBuffer
                                      ? new Uint8Array(event.data)
                                      : event.data,
                              );
                    data = JSON.parse(textData);
                } catch (error) {
                    logger.warn(
                        "Received unparseable JSON message on RTC stream data channel",
                        {
                            error,
                            channel: channel.label,
                        },
                    );
                    return;
                }
            }

            // Receive as either a chunk, full message, or H264 video frame
            if (data.communicationType === "message-chunk") {
                this.receiveChannelMessageChunk(
                    channel,
                    data as IRtcStreamMessageChunk,
                );
            } else {
                const message = data as IRtcStreamMessage;

                // Check if this is an H264VideoFrame message coming via JSON (legacy mode)
                if (message.payload?.h264VideoFrame) {
                    // Convert JSON H264VideoFrame to binary format for consistent handling
                    const legacyMessage: IBinaryH264Message = {
                        communicationType: "binary-h264",
                        timestamp: message.header?.created || Date.now(),
                        streamName:
                            message.header?.stream?.streamName || channel.label,
                        frame: message.payload.h264VideoFrame,
                    };
                    this.receiveBinaryH264Message(channel, legacyMessage);
                } else {
                    this.receiveChannelMessage(channel, message);
                }
            }
        };

        channel.onerror = (event: any) => {
            logger.warn("Channel error", {
                error: event.error,
                sessionId: this.getSessionId(),
                channelLabel: channel.label,
            });
        };

        channel.onopen = () => {
            logger.debug("Channel opened", {
                sessionId: this.getSessionId(),
                channelLabel: channel.label,
            });
        };

        channel.onclose = () => {
            const meta = {
                bufferedAmount: channel.bufferedAmount,
                sessionId: this.getSessionId(),
                channelLabel: channel.label,
            };
            if (this.closeCalled === false) {
                logger.debug("Unexpected channel closed", meta);
            } else {
                logger.debug("Channel closed", meta);
            }
        };
    }

    private receiveChannelMessage(
        channel: RTCDataChannel,
        message: IRtcStreamMessage,
    ) {
        const { config } = this;
        const { remotePeerId } = this.config.baseConfig;
        this.lastMessageTimestamp = new Date().getTime();

        // Sets values that were omitted by the JSON marshal/unmarshal process to zero
        message = reifyZeroValues(message);

        this.sessionMetrics?.incrementLocalReceived(message);
        if (this.handleSystemMessage(channel, message)) {
            return;
        }

        const isLatestChannel =
            channel === this.latestTryOnceStreamChannel ||
            channel === this.latestTtlStreamChannel ||
            channel === this.latestReliableStreamChannel;
        if (isLatestChannel && !this.isLatestMessage(message)) {
            return;
        }

        config.baseConfig.receive(remotePeerId, message);
    }

    private receiveChannelMessageChunk(
        channel: RTCDataChannel,
        chunk: IRtcStreamMessageChunk,
    ) {
        const entry = this.reassemblyTable.get(chunk.id) || [];
        entry.push(chunk);

        if (entry.length === chunk.total) {
            const message = reassembleMessageChunks(entry);
            if (message) {
                this.receiveChannelMessage(channel, message);
            }
            this.reassemblyTable.delete(chunk.id);
            return;
        }

        this.reassemblyTable.set(chunk.id, entry);
        this.reassemblyTableLastTimestamp.set(chunk.id, new Date().getTime());
    }

    private receiveBinaryH264Message(
        channel: RTCDataChannel,
        binaryMessage: IBinaryH264Message,
    ) {
        // Convert the binary H264 message to the standard message format
        // The stream name is now preserved in the binary message
        const rtcMessage: IRtcStreamMessage = {
            communicationType: "message",
            header: {
                stream: {
                    entityId: this.config.baseConfig.remotePeerId || "",
                    streamName: binaryMessage.streamName, // Now preserved from binary format!
                    streamType: "h264-video-frame",
                },
                created: binaryMessage.timestamp,
                frameId: "",
            },
            payload: {
                h264VideoFrame: binaryMessage.frame,
            },
        };

        // Process through the standard message handler
        this.receiveChannelMessage(channel, rtcMessage);
    }

    private receiveBinaryChunkMessage(
        channel: RTCDataChannel,
        binaryChunk: IBinaryChunkMessage,
    ) {
        // Handle binary chunks - reassemble them into a complete binary message
        const chunkId = binaryChunk.chunkId;
        const binaryChunks =
            this.binaryReassemblyTable.get(chunkId) || new Map();

        binaryChunks.set(binaryChunk.sequence, binaryChunk.data);

        if (binaryChunks.size === binaryChunk.total) {
            // All chunks received, reassemble the complete message
            let totalLength = 0;
            for (const chunk of binaryChunks.values()) {
                totalLength += chunk.length;
            }

            const completeData = new Uint8Array(totalLength);
            let offset = 0;

            for (let i = 0; i < binaryChunk.total; i++) {
                const chunk = binaryChunks.get(i);
                if (chunk) {
                    completeData.set(chunk, offset);
                    offset += chunk.length;
                }
            }

            // Parse the reassembled binary message
            try {
                if (isBinaryH264Message(completeData.buffer)) {
                    const binaryMessage = parseBinaryH264Message(
                        completeData.buffer,
                    );
                    this.receiveBinaryH264Message(channel, binaryMessage);
                }
            } catch (error) {
                logger.warn("Failed to parse reassembled binary message", {
                    error,
                    chunkId,
                    channel: channel.label,
                });
            }

            this.binaryReassemblyTable.delete(chunkId);
            this.binaryReassemblyTableLastTimestamp.delete(chunkId);
            return;
        }

        this.binaryReassemblyTable.set(chunkId, binaryChunks);
        this.binaryReassemblyTableLastTimestamp.set(
            chunkId,
            new Date().getTime(),
        );
    }

    /**
     * Handles system messages (e.g., ping/pong).
     * Returns true if the message is a system message.
     */
    private handleSystemMessage(
        channel: RTCDataChannel,
        message: IRtcStreamMessage,
    ): boolean {
        const { config } = this;
        const { baseConfig } = config;
        const { remotePeerId, remoteDeviceId } = baseConfig;

        const type = message.header.stream.streamType;

        switch (type) {
            case rtcPingStreamType: {
                this.sendSystemMessage(channel, {
                    type: rtcPongStreamType,
                    timestamp: defined(message.payload.ping),
                });
                return true;
            }
            case rtcPongStreamType: {
                const pingTime =
                    new Date().getTime() - defined(message.payload.pong);
                this.pingTimeMs = pingTime;
                stats.aggregate("rtc_ping_time", pingTime, {
                    ...(remoteDeviceId ? { deviceId: remoteDeviceId } : {}),
                });
                return true;
            }
            case rtcPingV2StreamType:
                this.sendPingV2();
                return true;
            case rtcPongV2StreamType: {
                const pong = defined(message.payload.pongV2);
                const currentEntry = this.pingV2Map.get(pong.id);
                if (currentEntry?.sent !== pong.timestamp) {
                    logger.warn("Pong timestamp doesn't match stored value", {
                        currentEntry,
                        pong,
                    });
                }
                this.pingV2Map.set(pong.id, {
                    sent: pong.timestamp,
                    received: new Date().getTime(),
                });

                return true;
            }
            case rtcStreamsInfoStreamType: {
                const { sessionMetrics } = this;
                const timestamp = message.payload.streamsInfo?.timestamp;
                if (!timestamp) {
                    return false;
                }
                sessionMetrics?.updateRemoteMessagesCounts(message);
                baseConfig.onStreamsInfoUpdate?.(remotePeerId, timestamp);
                return true;
            }
            case rtcStreamControlStreamType:
                // Not implemented; all streams are enabled by default coming from the typescript client.
                return true;
            default:
                return false;
        }
    }

    private sendSystemMessage(
        channel: RTCDataChannel,
        systemData:
            | { type: typeof rtcPingStreamType }
            | { type: typeof rtcPongStreamType; timestamp: Timestamp }
            | { type: typeof rtcPingV2StreamType; payload: IPingPayload }
            | { type: typeof rtcPongV2StreamType; payload: IPingPayload }
            | {
                  type: typeof rtcStreamControlStreamType;
                  streamControl: IStreamControl;
              }
            | {
                  type: typeof rtcStreamsInfoStreamType;
              },
    ) {
        const { localUserId, localPeerId } = this.config.baseConfig;

        if (channel.readyState !== "open") {
            return;
        }

        // The stream we will send the system message on
        const stream = {
            entityId: localUserId ?? localPeerId,
            streamName: `$.${systemData.type}`,
            streamType: systemData.type,
        };

        // The payload of our system message
        let payload: IRtcStreamPayload;
        switch (systemData.type) {
            case rtcPingStreamType:
                payload = { ping: new Date().getTime() };
                break;
            case rtcPongStreamType:
                payload = { pong: systemData.timestamp };
                break;
            case rtcPingV2StreamType:
                payload = { pingV2: systemData.payload };
                break;
            case rtcPongV2StreamType:
                payload = { pongV2: systemData.payload };
                break;
            case rtcStreamControlStreamType:
                payload = { streamControl: systemData.streamControl };
                break;
            case rtcStreamsInfoStreamType:
                payload = {
                    streamsInfo: this.sessionMetrics?.getLocalStreamsInfo(
                        new Date().getTime(),
                    ),
                };
                break;
        }

        this.sendOnChannel(channel, createRtcStreamMessage(stream, payload));
    }

    private isLatestMessage(message: IRtcStreamMessage) {
        const streamId = encodeStreamId(message.header.stream);
        const latest = this.streamLatestTimestamp.get(streamId) || 0;
        const isLatest = latest <= message.header.created;

        if (isLatest) {
            this.streamLatestTimestamp.set(streamId, message.header.created);
        }

        return isLatest;
    }

    private sendPingV2 = () => {
        const { latestTryOnceStreamChannel } = this;
        if (!latestTryOnceStreamChannel) {
            return;
        }
        const timestamp = new Date().getTime();
        const id = v4();
        this.sendSystemMessage(latestTryOnceStreamChannel, {
            type: rtcPingV2StreamType,
            payload: {
                timestamp,
                id,
            },
        });
        this.pingV2Map.set(id, { sent: timestamp });
    };

    private gatherPingV2Metrics = () => {
        const result = gatherPingV2Metrics(this.pingV2Map);
        if (result) {
            const deviceId = this.getRemoteDeviceId();
            this.pingInfo = result;
            stats.aggregate("rtc_ping_average", result.average, {
                ...(deviceId ? { deviceId } : {}),
            });
            stats.aggregate("rtc_ping_loss", result.loss, {
                ...(deviceId ? { deviceId } : {}),
            });
            stats.aggregate("rtc_jitter", result.jitter, {
                ...(deviceId ? { deviceId } : {}),
            });
        }
    };
}

function safeClearInterval(timeout: Timeout | undefined) {
    if (timeout) {
        clearInterval(timeout);
    }
}
